import { useState, useCallback, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import {
  X,
  Check,
  Gift,
  Trophy,
  BarChart,
  Heart,
  Lock,
  Sparkles
} from "lucide-react";
import { cn } from "@/lib/utils";

// Survey option types
interface SurveyOption {
  id: string;
  label: string;
}

// Survey data structure
export interface OnboardingSurveyData {
  howDidYouHear?: string;
  lookingToDo?: string;
  mobileNumber?: string;
}

interface OnboardingSurveyPopupProps {
  open: boolean;
  onClose: () => void;
  onStepComplete: (data: Partial<OnboardingSurveyData>) => Promise<void>;
  initialData?: OnboardingSurveyData;
}

// Step 1 options - How did you hear about us?
const HOW_DID_YOU_HEAR_OPTIONS: SurveyOption[] = [
  { id: "facebook_instagram", label: "Facebook or Instagram" },
  { id: "google", label: "Google" },
  { id: "reddit", label: "Reddit" },
  { id: "clover_app_market", label: "Clover App Market" },
  { id: "friend", label: "A friend told me" },
  { id: "other", label: "Other" }
];

// Step 2 options - What are you looking to do?
const LOOKING_TO_DO_OPTIONS: SurveyOption[] = [
  {
    id: "bring_customers",
    label: "Bring in more customers or increase repeat visits"
  },
  { id: "send_sms", label: "Send SMS to my customers" },
  { id: "understand_customers", label: "Understand my customers better" },
  { id: "product_performance", label: "See which products are doing well" },
  { id: "manage_customers", label: "Manage customer details" },
  { id: "other", label: "Other" }
];

// Country codes for phone input
const COUNTRY_CODES = [
  { id: "US", code: "+1", label: "US", flag: "🇺🇸" },
  { id: "CA", code: "+1", label: "CA", flag: "🇨🇦" },
  { id: "UK", code: "+44", label: "UK", flag: "🇬🇧" },
  { id: "IN", code: "+91", label: "IN", flag: "🇮🇳" },
  { id: "AU", code: "+61", label: "AU", flag: "🇦🇺" }
];

// Reusable Option Card Component
interface OptionCardProps {
  selected: boolean;
  onClick: () => void;
  label: string;
  multiSelect?: boolean;
}

const OptionCard = ({
  selected,
  onClick,
  label,
  multiSelect = false
}: OptionCardProps) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "flex items-center gap-3 w-full p-4 rounded-lg border-2 transition-all duration-200 text-left",
      "hover:border-[var(--primary)] hover:bg-[var(--primary)]/5",
      selected
        ? "border-[var(--primary)] bg-[var(--primary)]/5 text-[var(--primary)]"
        : "border-gray-200 bg-white text-gray-700"
    )}
  >
    {multiSelect && (
      <div
        className={cn(
          "flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all",
          selected
            ? "border-[var(--primary)] bg-[var(--primary)]"
            : "border-gray-300"
        )}
      >
        {selected && <Check className="w-3 h-3 text-white" />}
      </div>
    )}
    <span className="text-sm font-medium">{label}</span>
  </button>
);

// Progress Bar Component
interface ProgressBarProps {
  currentStep: number;
  totalSteps: number;
}

const ProgressBar = ({ currentStep, totalSteps }: ProgressBarProps) => (
  <div className="flex gap-2">
    {Array.from({ length: totalSteps }).map((_, index) => (
      <div
        key={index}
        className={cn(
          "h-1 flex-1 rounded-full transition-colors duration-300",
          index < currentStep ? "bg-[var(--primary)]" : "bg-gray-200"
        )}
      />
    ))}
  </div>
);

export const OnboardingSurveyPopup = ({
  open,
  onClose,
  onStepComplete,
  initialData = {}
}: OnboardingSurveyPopupProps) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [howDidYouHear, setHowDidYouHear] = useState<string>(
    initialData.howDidYouHear || ""
  );
  const [lookingToDo, setLookingToDo] = useState<Set<string>>(
    new Set(initialData.lookingToDo?.split(",").filter(Boolean) || [])
  );
  const [selectedCountry, setSelectedCountry] = useState("US");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [phoneError, setPhoneError] = useState("");

  // "Other" text inputs for step 1 and 2
  const [otherHowDidYouHear, setOtherHowDidYouHear] = useState("");
  const [otherLookingToDo, setOtherLookingToDo] = useState("");

  const totalSteps = 3;

  // Clear phone error when navigating away from step 3
  useEffect(() => {
    if (currentStep !== 3) {
      setPhoneError("");
    }
  }, [currentStep]);

  // Format phone for display
  const formatPhoneDisplay = useCallback((value: string) => {
    const digits = value.replace(/\D/g, "");
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  }, []);

  // Handle phone input change
  const handlePhoneChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value.replace(/\D/g, "").slice(0, 10);
      setPhoneNumber(value);
      // Clear error when user starts typing
      if (phoneError) {
        setPhoneError("");
      }
    },
    [phoneError]
  );

  // Toggle multi-select option
  const toggleLookingToDoOption = useCallback((optionId: string) => {
    setLookingToDo((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(optionId)) {
        newSet.delete(optionId);
      } else {
        newSet.add(optionId);
      }
      return newSet;
    });
  }, []);

  // Save current step data
  const saveCurrentStepData = useCallback(async () => {
    setIsSubmitting(true);
    try {
      const data: Partial<OnboardingSurveyData> = {};

      if (currentStep >= 1 && howDidYouHear) {
        // If "other" is selected, append the custom text
        if (howDidYouHear === "other" && otherHowDidYouHear.trim()) {
          data.howDidYouHear = `other:${otherHowDidYouHear.trim()}`;
        } else {
          data.howDidYouHear = howDidYouHear;
        }
      }
      if (currentStep >= 2 && lookingToDo.size > 0) {
        // Build the lookingToDo string, replacing "other" with custom text if provided
        const selections = Array.from(lookingToDo).map((id) => {
          if (id === "other" && otherLookingToDo.trim()) {
            return `other:${otherLookingToDo.trim()}`;
          }
          return id;
        });
        data.lookingToDo = selections.join(",");
      }
      if (currentStep >= 3 && phoneNumber) {
        const country = COUNTRY_CODES.find((c) => c.id === selectedCountry);
        data.mobileNumber = `${country?.code || "+1"}${phoneNumber}`;
      }

      await onStepComplete(data);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    currentStep,
    howDidYouHear,
    otherHowDidYouHear,
    lookingToDo,
    otherLookingToDo,
    selectedCountry,
    phoneNumber,
    onStepComplete
  ]);

  // Handle next step
  const handleNext = useCallback(async () => {
    // On step 3 (final step), require phone number when clicking "Send My 2025 Wrapped"
    if (currentStep === totalSteps) {
      if (!phoneNumber || phoneNumber.length < 10) {
        setPhoneError("Phone number is required to send your 2025 Wrapped");
        return;
      }
    }

    await saveCurrentStepData();
    if (currentStep < totalSteps) {
      setCurrentStep((prev) => prev + 1);
    } else {
      onClose();
    }
  }, [currentStep, totalSteps, phoneNumber, saveCurrentStepData, onClose]);

  // Handle previous step
  const handlePrevious = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    }
  }, [currentStep]);

  // Handle skip
  const handleSkip = useCallback(async () => {
    await saveCurrentStepData();
    if (currentStep < totalSteps) {
      setCurrentStep((prev) => prev + 1);
    } else {
      onClose();
    }
  }, [currentStep, totalSteps, saveCurrentStepData, onClose]);

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <DialogTitle className="text-xl font-semibold text-gray-900">
                How did you hear about us?
              </DialogTitle>
              <DialogDescription className="text-sm text-gray-500">
                Many of our users find us on Facebook or Instagram — how did you
                hear about us?
              </DialogDescription>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-6">
              {HOW_DID_YOU_HEAR_OPTIONS.map((option) => (
                <OptionCard
                  key={option.id}
                  selected={howDidYouHear === option.id}
                  onClick={() => setHowDidYouHear(option.id)}
                  label={option.label}
                />
              ))}
            </div>
            {/* Show input when "Other" is selected */}
            {howDidYouHear === "other" && (
              <Input
                type="text"
                placeholder="Type what you need..."
                value={otherHowDidYouHear}
                onChange={(e) => setOtherHowDidYouHear(e.target.value)}
                className="mt-4 focus-visible:ring-[var(--primary)] focus-visible:border-[var(--primary)]"
                autoFocus
              />
            )}
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <DialogTitle className="text-xl font-semibold text-gray-900">
                What are you looking to do?
              </DialogTitle>
              <DialogDescription className="text-sm text-gray-500">
                Select all that apply — we'll personalize your experience.
              </DialogDescription>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-6">
              {LOOKING_TO_DO_OPTIONS.map((option) => (
                <OptionCard
                  key={option.id}
                  selected={lookingToDo.has(option.id)}
                  onClick={() => toggleLookingToDoOption(option.id)}
                  label={option.label}
                  multiSelect
                />
              ))}
            </div>
            {/* Show input when "Other" is selected */}
            {lookingToDo.has("other") && (
              <Input
                type="text"
                placeholder="Type what you need..."
                value={otherLookingToDo}
                onChange={(e) => setOtherLookingToDo(e.target.value)}
                className="mt-4 focus-visible:ring-[var(--primary)] focus-visible:border-[var(--primary)]"
                autoFocus
              />
            )}
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            {/* Gift Icon Header */}
            <div className="flex flex-col items-center space-y-2">
              <div className="w-16 h-16 rounded-full bg-gradient-to-r from-red-500 to-green-500 flex items-center justify-center shadow-sm">
                <Gift className="w-8 h-8 text-yellow-400" />
              </div>
              <DialogTitle className="text-xl font-bold text-gray-900 text-center">
                Your 2025 Business Wrapped is Ready!
              </DialogTitle>
            </div>

            {/* Highlights Section */}
            <div className="space-y-2 p-3 rounded-3xl bg-gradient-to-b from-pink-100/40 via-pink-50/20 to-green-100/40">
              <h3 className="text-center text-sm font-semibold text-red-500">
                ✨ 2025 HIGHLIGHTS ✨
              </h3>
              <div className="grid grid-cols-3 gap-2">
                {/* Top Seller Card */}
                <div className="flex flex-col items-center justify-center p-2 rounded-lg border border-gray-200 bg-white">
                  <Trophy className="w-6 h-6 text-yellow-500 mb-1" />
                  <span className="text-xs font-medium text-gray-700 text-center">
                    Top Seller
                  </span>
                </div>
                {/* Busiest Day Card */}
                <div className="flex flex-col items-center justify-center p-2 rounded-lg border border-gray-200 bg-white">
                  <BarChart className="w-6 h-6 text-blue-500 mb-1" />
                  <span className="text-xs font-medium text-gray-700 text-center">
                    Busiest Day
                  </span>
                </div>
                {/* Top Fans Card */}
                <div className="flex flex-col items-center justify-center p-2 rounded-lg border border-gray-200 bg-white">
                  <Heart className="w-6 h-6 text-pink-500 mb-1" />
                  <span className="text-xs font-medium text-gray-700 text-center">
                    Top Fans
                  </span>
                </div>
              </div>
            </div>

            {/* Phone Number Input */}
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-700 text-center">
                📱 Enter your phone number:
              </p>
              <div className="flex gap-2">
                <Select
                  value={selectedCountry}
                  onValueChange={setSelectedCountry}
                >
                  <SelectTrigger className="w-28 h-9 focus:ring-[var(--primary)] focus:border-[var(--primary)] border-gray-300">
                    <SelectValue>
                      {(() => {
                        const country = COUNTRY_CODES.find(
                          (c) => c.id === selectedCountry
                        );
                        return country ? (
                          <span className="flex items-center gap-1 text-sm lowercase">
                            <span>{country.label.toLowerCase()}</span>
                            <span className="text-gray-500">
                              {country.code}
                            </span>
                          </span>
                        ) : null;
                      })()}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRY_CODES.map((country) => (
                      <SelectItem key={country.id} value={country.id}>
                        <span className="flex items-center gap-2">
                          <span>{country.label}</span>
                          <span className="text-gray-500">{country.code}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  type="tel"
                  placeholder="(555) 123-4567"
                  value={formatPhoneDisplay(phoneNumber)}
                  onChange={handlePhoneChange}
                  className={cn(
                    "flex-1 h-9 focus-visible:ring-[var(--primary)] focus-visible:border-[var(--primary)]",
                    phoneError
                      ? "border-red-500 focus-visible:border-red-500 focus-visible:ring-red-500"
                      : "border-gray-300"
                  )}
                />
              </div>
              {phoneError && (
                <p className="text-xs text-red-500 text-center mt-1">
                  {phoneError}
                </p>
              )}
              <div className="flex items-center justify-center gap-1.5 text-xs text-gray-500">
                <Lock className="w-3 h-3 flex-shrink-0" />
                <span>
                  We'll only use this to send your report. Safe & secure.
                </span>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <style>{`
        [data-radix-dialog-overlay] {
          backdrop-filter: blur(4px);
        }
      `}</style>
      <DialogContent
        className="max-w-[540px] p-0 gap-0 border-0 bg-white overflow-hidden rounded-2xl flex flex-col max-h-[90vh]"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none z-10"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="p-4 pb-2 flex-shrink-0">
          {/* Step indicator */}
          <p className="text-sm text-gray-500 mb-1.5">
            Step {currentStep} of {totalSteps}
          </p>

          {/* Progress bar */}
          <ProgressBar currentStep={currentStep} totalSteps={totalSteps} />
        </div>

        {/* Scrollable content */}
        <div className="px-4 pb-2 overflow-y-auto flex-1 min-h-0">
          {renderStepContent()}
        </div>

        {/* Footer - Fixed at bottom */}
        <div className="grid grid-cols-3 items-center px-4 py-3 border-t border-gray-100 flex-shrink-0">
          {/* Previous - Left */}
          <div className="flex justify-start">
            <Button
              variant="ghost"
              onClick={handlePrevious}
              disabled={currentStep === 1}
              className={cn(
                "text-gray-600 hover:text-gray-800",
                currentStep === 1 && "opacity-50 cursor-not-allowed"
              )}
            >
              Previous
            </Button>
          </div>
          {/* Skip - Center */}
          <div className="flex justify-start -ml-8">
            <Button
              variant="ghost"
              onClick={handleSkip}
              disabled={isSubmitting}
              className="text-gray-600 hover:text-gray-800"
            >
              Skip
            </Button>
          </div>
          {/* Send/Next - Right */}
          <div className="flex justify-end">
            <Button
              onClick={handleNext}
              disabled={isSubmitting}
              className={cn(
                "px-6 py-2.5 rounded-full text-white font-medium transition-all",
                currentStep === totalSteps
                  ? "bg-gradient-to-r from-red-500 to-green-500 hover:from-red-600 hover:to-green-600 shadow-lg hover:shadow-xl"
                  : "bg-[var(--primary)] hover:bg-[var(--primary)]/90"
              )}
            >
              {isSubmitting ? (
                "Saving..."
              ) : currentStep === totalSteps ? (
                <span className="flex items-center gap-1.5">
                  Send My 2025 Wrapped <Sparkles className="w-4 h-4" />
                </span>
              ) : (
                "Next"
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OnboardingSurveyPopup;
